import React, { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import SportsbookLogo, { SPORTSBOOK_NAMES } from './SportsbookLogo';
import TeamLogo from './TeamLogo';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const SPORTSBOOK_URLS = {
  draftkings: 'https://sportsbook.draftkings.com',
  fanduel: 'https://sportsbook.fanduel.com',
  betmgm: 'https://sports.betmgm.com',
  williamhill_us: 'https://www.caesars.com/sportsbook-and-casino',
  fanatics: 'https://sportsbook.fanatics.com',
  espnbet: 'https://sportsbook.thescore.bet',
  ballybet: 'https://www.ballybet.com',
  betrivers: 'https://www.betrivers.com',
};

function formatOdds(odds) {
  if (odds == null) return '—';
  return odds > 0 ? `+${odds}` : `${odds}`;
}

function formatDollars(n) {
  if (n == null) return '—';
  return `$${n.toFixed(2)}`;
}

function naturalRound(amount) {
  if (amount == null) return null;
  const increment = amount >= 1000 ? 10 : amount >= 100 ? 5 : 1;
  return Math.round(amount / increment) * increment;
}

function oddsToDecimal(american) {
  if (!american) return 2;
  if (american > 0) return american / 100 + 1;
  return 100 / Math.abs(american) + 1;
}

function getSportIcon(sportKey) {
  if (!sportKey) return '🏆';
  if (sportKey.includes('football')) return '🏈';
  if (sportKey.includes('basketball')) return '🏀';
  if (sportKey.includes('hockey')) return '🏒';
  if (sportKey.includes('soccer')) return '⚽';
  if (sportKey.includes('tennis')) return '🎾';
  if (sportKey.includes('mma')) return '🥊';
  if (sportKey.includes('baseball')) return '⚾';
  if (sportKey.includes('boxing')) return '🥊';
  return '🏆';
}

function getMarketLabel(market) {
  if (!market || market === 'h2h') return 'Moneyline';
  if (market === 'spreads') return 'Spread';
  if (market === 'totals') return 'Total';
  if (market === 'props') return 'Props';
  return market;
}

function formatSide(side, market) {
  if (!side) return side;
  if (market === 'spreads') {
    const match = side.match(/^(.*?)(\s[+-]\d+\.?\d*)$/);
    if (match) {
      return <>{match[1]}<span className="pc-line">{match[2]}</span></>;
    }
  }
  if (market === 'totals') {
    const match = side.match(/^(Over|Under)\s+(\d+\.?\d*)$/);
    if (match) {
      return <><span className="pc-ou">{match[1]}</span><span className="pc-line"> {match[2]}</span></>;
    }
  }
  return side;
}

function BookLink({ bookKey, bookTitle }) {
  return (
    <div className="pc-book-row">
      <SportsbookLogo bookKey={bookKey} size={16} />
      <span className="pc-book-name">{SPORTSBOOK_NAMES[bookKey] || bookTitle}</span>
      {SPORTSBOOK_URLS[bookKey] && (
        <a
          href={SPORTSBOOK_URLS[bookKey]}
          target="_blank"
          rel="noopener noreferrer"
          className="book-link"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink size={11} />
        </a>
      )}
    </div>
  );
}

function typeLabel(promoType) {
  if (promoType === 'free_bet') return 'free bet';
  if (promoType === 'no_sweat') return 'no sweat';
  if (promoType === 'profit_boost') return 'boosted bet';
  return promoType;
}


function HedgePairCard({ pair, session }) {
  const tier = pair.min_guaranteed_profit >= 40 ? 'high'
             : pair.min_guaranteed_profit >= 20 ? 'medium' : 'low';

  const balancePct = Math.round((pair.balance_ratio ?? 1) * 100);
  const balanceClass = balancePct >= 70 ? 'hpc-balance-high' : 'hpc-balance-low';

  const isArb = pair.pair_type === 'arb';
  const isAsymmetric = pair.pair_type === 'asymmetric';
  const isSymmetric = pair.pair_type === 'symmetric';

  // Free money flags: free_bet stakes are not real money (no_sweat stakes are real money upfront)
  const isFreeMoneyA = pair.promo_a_type === 'free_bet';
  const isFreeMoneyB = pair.promo_b_type === 'free_bet';

  const [tracked, setTracked] = useState(false);
  const [trackingMode, setTrackingMode] = useState(false);
  const [amountA, setAmountA] = useState('');
  const [amountB, setAmountB] = useState('');

  let heroSecondary;
  if (isArb) {
    heroSecondary = `Costs ${formatDollars(pair.hedge_cost)} real money · ${pair.arb_profit_pct}% guaranteed`;
  } else if (isAsymmetric) {
    heroSecondary = `Costs ${formatDollars(pair.hedge_cost)} real money on boost side`;
  } else {
    heroSecondary = 'No real money required';
  }

  const decA = oddsToDecimal(pair.promo_a_odds);
  const decB = oddsToDecimal(pair.promo_b_odds);
  const aA = parseFloat(amountA) || 0;
  const aB = parseFloat(amountB) || 0;
  const showPreview = aA > 0 || aB > 0;
  const profitIfAWins = aA * (decA - 1) - (isFreeMoneyB ? 0 : aB);
  const profitIfBWins = aB * (decB - 1) - (isFreeMoneyA ? 0 : aA);

  async function handleTrack(e) {
    e.stopPropagation();
    if (!session) return;

    const market = pair.market || 'h2h';
    const rawA = market === 'totals'
      ? (pair.promo_a_side?.startsWith('Over') ? 'Over' : 'Under')
      : market === 'spreads'
      ? (pair.home_team && pair.promo_a_side?.startsWith(pair.home_team) ? pair.home_team : pair.away_team) || pair.promo_a_side
      : pair.promo_a_side;
    const rawB = market === 'totals'
      ? (pair.promo_b_side?.startsWith('Over') ? 'Over' : 'Under')
      : market === 'spreads'
      ? (pair.home_team && pair.promo_b_side?.startsWith(pair.home_team) ? pair.home_team : pair.away_team) || pair.promo_b_side
      : pair.promo_b_side;

    try {
      await fetch(`${API_BASE}/user/bets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          betType: 'promo',
          sportsbook: pair.promo_a_book,
          eventName: pair.event,
          sportKey: pair.sport,
          market,
          side: pair.promo_a_side,
          odds: pair.promo_a_odds,
          amount: parseFloat(amountA) || 0,
          sportsbookB: pair.promo_b_book,
          sideB: pair.promo_b_side,
          oddsB: pair.promo_b_odds,
          amountB: parseFloat(amountB) || 0,
          notes: JSON.stringify({
            event_id: pair.event_id,
            sport_key: pair.sport,
            commence_time: pair.commence_time,
            promo_type: pair.promo_a_type,
            is_free_money_b: isFreeMoneyB,
            pair_type: pair.pair_type,
            home_team: pair.home_team,
            away_team: pair.away_team,
            point: pair.point ?? null,
            side_a: rawA,
            side_b: rawB,
          }),
        }),
      });
      setTrackingMode(false);
      setTracked(true);
    } catch (err) {
      console.error('Track failed:', err);
    }
  }

  return (
    <div className={`promo-card promo-${tier}`}>

      <div className="pc-header">
        <span className="pc-sport">{getSportIcon(pair.sport)} {pair.sport_title || 'Sports'}</span>
        <span className="pc-market">{getMarketLabel(pair.market)}</span>
      </div>

      <div className="pc-event">
        <span className="mini-game-team"><TeamLogo teamName={pair.home_team} sportKey={pair.sport} size={13} />{pair.home_team}</span>
        <span className="mini-game-sep">vs</span>
        <span className="mini-game-team"><TeamLogo teamName={pair.away_team} sportKey={pair.sport} size={13} />{pair.away_team}</span>
      </div>
      {pair.commence_time && (
        <div className="pc-game-time">
          {new Date(pair.commence_time).toLocaleString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
          })}
        </div>
      )}

      <div className={`pc-hero pc-hero-${tier}`}>
        <div className="pc-hero-main">
          <span className="pc-hero-label">Min profit</span>
          <span className="pc-hero-amount">{formatDollars(pair.min_guaranteed_profit)}</span>
        </div>
        <span className="pc-hero-secondary">{heroSecondary}</span>
      </div>

      {isSymmetric && (
        <div className={`hpc-balance-badge ${balanceClass}`}>
          {balancePct}% balanced — {balancePct >= 70 ? 'well-matched' : 'uneven outcomes'}
        </div>
      )}

      {(isArb || isAsymmetric) && (
        <div className="hpc-cost-badge">
          Requires {formatDollars(pair.hedge_cost)} real money
        </div>
      )}

      {/* Side A */}
      <div className="pc-step">
        <span className="pc-step-num">A</span>
        <div className="pc-step-body">
          <BookLink bookKey={pair.promo_a_book} bookTitle={pair.promo_a_book_title} />
          <div className="pc-step-bet">
            <span className="pc-side">{formatSide(pair.promo_a_side, pair.market)}</span>
            <div className="pc-step-odds-amount">
              <span className="pc-odds">{formatOdds(pair.promo_a_odds)}</span>
              <span className="pc-divider">·</span>
              <span className="pc-amount">{formatDollars(naturalRound(pair.promo_a_amount))}</span>
              <span className="pc-amount-note">{typeLabel(pair.promo_a_type)}</span>
            </div>
          </div>
          <div className="hpc-outcome">if wins: +{formatDollars(pair.profit_if_a_wins)}</div>
        </div>
      </div>

      {/* Side B */}
      <div className="pc-step">
        <span className="pc-step-num">B</span>
        <div className="pc-step-body">
          <BookLink bookKey={pair.promo_b_book} bookTitle={pair.promo_b_book_title} />
          <div className="pc-step-bet">
            <span className="pc-side">{formatSide(pair.promo_b_side, pair.market)}</span>
            <div className="pc-step-odds-amount">
              <span className="pc-odds">{formatOdds(pair.promo_b_odds)}</span>
              <span className="pc-divider">·</span>
              <span className="pc-amount">{formatDollars(naturalRound(pair.promo_b_amount))}</span>
              <span className="pc-amount-note">{typeLabel(pair.promo_b_type)}</span>
            </div>
          </div>
          <div className="hpc-outcome">if wins: +{formatDollars(pair.profit_if_b_wins)}</div>
        </div>
      </div>

      {session && !tracked && !trackingMode && (
        <button
          className="ev-track-btn"
          onClick={(e) => {
            e.stopPropagation();
            setAmountA(pair.promo_a_amount != null ? String(naturalRound(pair.promo_a_amount)) : '');
            setAmountB(pair.promo_b_amount != null ? String(naturalRound(pair.promo_b_amount)) : '');
            setTrackingMode(true);
          }}
        >
          Track Bet
        </button>
      )}

      {session && trackingMode && (
        <div className="arb-track-form" onClick={e => e.stopPropagation()}>
          <div className="arb-track-row">
            <span className="arb-track-label">{pair.promo_a_book_title || pair.promo_a_book}</span>
            <input
              type="number"
              className="track-amount-input"
              value={amountA}
              onChange={e => setAmountA(e.target.value)}
              placeholder="Side A stake ($)"
              min="0"
              step="0.01"
              autoFocus
            />
          </div>
          <div className="arb-track-row">
            <span className="arb-track-label">{pair.promo_b_book_title || pair.promo_b_book}</span>
            <input
              type="number"
              className="track-amount-input"
              value={amountB}
              onChange={e => setAmountB(e.target.value)}
              placeholder="Side B stake ($)"
              min="0"
              step="0.01"
            />
          </div>
          {showPreview && (
            <div className="arb-profit-preview">
              <div className="arb-profit-preview-row">
                <span>A wins:</span>
                <span className={profitIfAWins >= 0 ? 'profit-pos' : 'profit-neg'}>
                  {profitIfAWins >= 0 ? '+' : ''}${profitIfAWins.toFixed(2)}
                </span>
              </div>
              <div className="arb-profit-preview-row">
                <span>B wins:</span>
                <span className={profitIfBWins >= 0 ? 'profit-pos' : 'profit-neg'}>
                  {profitIfBWins >= 0 ? '+' : ''}${profitIfBWins.toFixed(2)}
                </span>
              </div>
            </div>
          )}
          <div className="track-amount-btns">
            <button className="track-amount-save" onClick={handleTrack}>Track</button>
            <button className="track-amount-cancel" onClick={(e) => { e.stopPropagation(); setTrackingMode(false); }}>✕</button>
          </div>
        </div>
      )}

      {session && tracked && (
        <button className="ev-track-btn ev-track-btn--done" onClick={e => e.stopPropagation()} disabled>Tracked ✓</button>
      )}

    </div>
  );
}

export default HedgePairCard;
